import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { PermissionService } from 'app/components/permission-modal/permission.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { AnnouncementService } from 'app/modules/announcement/announcement/announcement.service';
import { UserBService } from 'app/modules/ok-admin/misc/user-b.service';
import { AdminProductSearchDTO } from 'app/modules/products/list-products/product';
import { UpdateMetricsService } from 'app/services/updateMetrics/updateMetrics.service';
import { cloneDeep } from 'lodash-es';
import { forkJoin, Observable, ReplaySubject, tap } from 'rxjs';
import { FuseNavigationItem } from '../../../@fuse/components/navigation';
import {
    compactNavigation,
    futuristicNavigation,
    horizontalNavigation,
} from '../../mock-api/common/navigation/data';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);
    private _compactNavigation: FuseNavigationItem[] = compactNavigation;
    private _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
    private _horizontalNavigation: FuseNavigationItem[] = horizontalNavigation;
    private _announcementService = inject(AnnouncementService);
    private _triggerService = inject(UpdateMetricsService);

    totalAmount: number = 0;
    totalAmountPending: number = 0;

    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    constructor(
        private userBService: UserBService,
        private permissionService: PermissionService
    ) {
        this._triggerService.change$.subscribe(() => {
            this.search();
        });
    }

    get(): Observable<Navigation> {
        this.search();
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            tap((navigation) => {
                let x = this.registerHandlers();
                this._navigation.next(x);
            })
        );
    }

    getItemByUrl(url: string, items: any[]): any | null {
        const baseUrl = url.split(/[?#]/)[0];

        for (const item of items) {
            if (item.link === baseUrl) return item;

            if (item.children) {
                const found = this.getItemByUrl(url, item.children);
                if (found) return found;
            }
        }
        return null;
    }

    searchParams: AdminProductSearchDTO = {
        page: 0,
        size: 20,
        q: '',
        status: undefined,
    };
    search() {
        const paramsAll = { ...this.searchParams, status: undefined };
        const paramsPending = { ...this.searchParams, status: 'PENDING' };

        forkJoin({
            all: this._announcementService.search(paramsAll),
            pending: this._announcementService.search(paramsPending),
        }).subscribe({
            next: (resp) => {
                this.totalAmount = resp.all.totalAmount;
                this.totalAmountPending = resp.pending.totalAmount;

                const updatedNav = this.registerHandlers();
                this._navigation.next(updatedNav);
            },
            error: (error) => {
                console.error('Erro ao buscar totais:', error);
            },
        });
    }

    registerHandlers(): any {
        const defaultNavigation: FuseNavigationItem[] = [
            {
                id: 'overview',
                title: 'Visão Geral',
                type: 'group',
                icon: 'heroicons_outline:chart-pie',
                children: [
                    {
                        id: 'dashboard',
                        title: 'Dashboard',
                        text: 'Visão geral do marketplace',
                        type: 'basic',
                        icon: 'mat_outline:dashboard',
                        link: '/dashboard',
                    }
                ]
            },
            {
                id: 'catalog',
                title: 'Catálogo',
                type: 'group',
                icon: 'heroicons_outline:view-columns',
                children: [
                    {
                        id: 'catalog.categories',
                        title: 'Categorias',
                        text: 'Gerencie as categorias',
                        type: 'basic',
                        icon: 'mat_outline:category',
                        link: '/categories',
                        hidden: () => this.permissionService.can('view_category'),
                    },
                    {
                        id: 'catalog.announcements',
                        title: 'Anúncios',
                        text: 'Gerencie todos os anúncios',
                        type: 'basic',
                        icon: 'mat_outline:text_snippet',
                        link: '/announcements',
                        badge: {
                            title: `${this.totalAmount ?? 0}`,
                            classes: 'min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-[#eb670f] text-white text-[10px] font-bold',
                        },
                        hidden: () => this.permissionService.can('view_ad'),
                    },
                    {
                        id: 'catalog.pending',
                        title: 'Em Aprovação',
                        text: 'Revise e aprove anúncios',
                        type: 'basic',
                        icon: 'mat_outline:approval',
                        link: '/pending',
                        badge: {
                            title: `${this.totalAmountPending ?? 0}`,
                            classes: 'min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-[#eb670f] text-white text-[10px] font-bold',
                        },
                        hidden: () => this.permissionService.can('approve_ad'),
                    }
                ]
            },
            {
                id: 'engagement',
                title: 'Comunicação',
                type: 'group',
                icon: 'heroicons_outline:chat-bubble-left-right',
                children: [
                    {
                        id: 'engagement.chat',
                        title: 'Chat de Atendimento',
                        type: 'basic',
                        icon: 'mat_outline:chat',
                        link: '/chat',
                        isActiveMatchOptions: {
                            queryParams: 'ignored',
                            matrixParams: 'ignored',
                            paths: 'exact',
                            fragment: 'ignored',
                        },
                        hidden: () => this.permissionService.can('view_chat'),
                    },
                    {
                        id: 'engagement.chat_moderation',
                        title: 'Moderação de Mensagens',
                        type: 'basic',
                        icon: 'mat_outline:security',
                        link: '/chat/moderation',
                    },
                    /*{
                        id: 'engagement.chat_metrics',
                        title: 'Métricas de Atendimento',
                        type: 'basic',
                        icon: 'mat_outline:bar_chart',
                        link: '/chat/metrics',
                        isActiveMatchOptions: {
                            queryParams: 'ignored',
                            matrixParams: 'ignored',
                            paths: 'exact',
                            fragment: 'ignored',
                        },
                        hidden: () => this.permissionService.can('view_chat'),
                    },*/
                    {
                        id: 'engagement.push',
                        title: 'Push Notifications',
                        type: 'basic',
                        icon: 'mat_outline:notifications_active',
                        link: '/push-notifications',
                        isActiveMatchOptions: {
                            queryParams: 'ignored',
                            matrixParams: 'ignored',
                            paths: 'exact',
                            fragment: 'ignored',
                        },
                    },
                    {
                        id: 'engagement.stories',
                        title: 'Stories Grátis',
                        type: 'basic',
                        icon: 'mat_outline:card_giftcard',
                        link: '/campaign-stories',
                        isActiveMatchOptions: {
                            queryParams: 'ignored',
                            matrixParams: 'ignored',
                            paths: 'exact',
                            fragment: 'ignored',
                        },
                        hidden: () => this.permissionService.can('view_stories'),
                    }
                ]
            },
            {
                id: 'users',
                title: 'Usuários',
                type: 'group',
                icon: 'heroicons_outline:users',
                children: [
                    {
                        id: 'users.adm',
                        title: 'Usuários',
                        text: 'Gerenciamento de Usuários',
                        type: 'basic',
                        icon: 'heroicons_outline:user',
                        link: '/users',
                        isActiveMatchOptions: {
                            queryParams: 'ignored',
                            matrixParams: 'ignored',
                            paths: 'exact',
                            fragment: 'ignored',
                        },
                        hidden: () => this.permissionService.can('create_user'),
                    },
                    {
                        id: 'users.backoffice',
                        title: 'Usuários Backoffice',
                        text: 'Usuários Backoffice',
                        type: 'basic',
                        icon: 'mat_outline:admin_panel_settings',
                        link: '/usersBackoffice',
                        hidden: () => this.permissionService.can('create_userb'),
                    },
                    {
                        id: 'users.user-reports',
                        title: 'Reporte de Usuários',
                        text: 'Problemas reportados',
                        type: 'basic',
                        icon: 'mat_outline:report_problem',
                        link: '/users/user-reports',
                    },
                    {
                        id: 'users.audit',
                        title: 'Auditoria',
                        text: 'Histórico de ações realizadas',
                        type: 'basic',
                        icon: 'heroicons_outline:shield-check',
                        link: '/audit',
                        isActiveMatchOptions: {
                            queryParams: 'ignored',
                            matrixParams: 'ignored',
                            paths: 'exact',
                            fragment: 'ignored',
                        },
                    }
                ],
            },
            {
                id: 'settings',
                title: 'Configurações',
                type: 'group',
                icon: 'heroicons_outline:cog',
                children: [
                    {
                        id: 'settings.verification-config',
                        title: 'Níveis & Verificação',
                        text: 'Limites de chat, WhatsApp e Nível 2',
                        type: 'basic',
                        icon: 'heroicons_outline:shield-check',
                        link: '/verification-config',
                    },
                    {
                        id: 'settings.featured-ads',
                        title: 'Anúncios Destacados',
                        text: 'Configuração dos destaques',
                        type: 'basic',
                        icon: 'heroicons_outline:star',
                        link: '/featured-ads-config',
                    },
                    {
                        id: 'settings.human-support',
                        title: 'Suporte Humano',
                        text: 'Atendentes e horários',
                        type: 'basic',
                        icon: 'mat_outline:headset',
                        link: '/settings/human-support',
                    },
                    {
                        id: 'settings.knowledge-base',
                        title: 'Base de Conhecimento',
                        type: 'basic',
                        icon: 'heroicons_outline:document-text',
                        link: '/settings/knowledge-base',
                    }

                ]
            }
        ];

        // Return the response
        return {
            compact: cloneDeep(this._compactNavigation),
            default: defaultNavigation,
            futuristic: cloneDeep(this._futuristicNavigation),
            horizontal: cloneDeep(this._horizontalNavigation),
        };
    }
}
